import React, { useState } from 'react';
import { FaLock, FaEye, FaEyeSlash, FaKey, FaShieldAlt } from 'react-icons/fa';
import { Shield, KeyRound, Eye, EyeOff, Check, AlertTriangle } from 'lucide-react';
import HomeToastNotification from './auth/loaders/HomeToastNotification';

const ChangePassword = ({ token }) => {
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });
  const [toastNotification, setToastNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === 'newPassword') {
      calculatePasswordStrength(value);
    }
    
    setToastNotification(null);
  };

  const calculatePasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }
    
    let strength = 0;
    
    // Length check
    if (password.length >= 8) strength += 1;
    
    // Character variety checks
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    
    setPasswordStrength(strength);
  };

  const getStrengthLabel = () => {
    if (passwordStrength === 0) return '';
    if (passwordStrength <= 2) return 'Weak';
    if (passwordStrength <= 4) return 'Medium';
    return 'Strong';
  };

  const getStrengthColor = () => {
    if (passwordStrength === 0) return '#ddd';
    if (passwordStrength <= 2) return '#f87171';
    if (passwordStrength <= 4) return '#fbbf24';
    return '#34d399';
  };

  const validateForm = () => {
    if (!formData.oldPassword || !formData.newPassword || !formData.confirmPassword) {
      setToastNotification({ message: 'All fields are required', type: 'error' });
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setToastNotification({ message: 'New passwords do not match', type: 'error' });
      return false;
    }
    if (formData.newPassword.length < 6) {
      setToastNotification({ message: 'New password must be at least 6 characters long', type: 'error' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const response = await fetch('https://api.visiontech2030.com/register/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to change password');
      }

      setToastNotification({ message: 'Password changed successfully', type: 'success' });
      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordStrength(0);
    } catch (err) {
      setToastNotification({ message: err.message || 'An error occurred while changing password', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="vt-password-container">
      <div className="vt-password-header">
        <div className="vt-password-header-glow"></div>
        <div className="vt-password-header-icon">
          <Shield size={24} />
        </div>
        <h1 className="vt-password-title">Security Settings</h1>
        <p className="vt-password-subtitle">Update your password to keep your account secure</p>
      </div>
      
      <div className="vt-password-card">
        <form onSubmit={handleSubmit} className="vt-password-form">
          <div className="vt-password-field">
            <label className="vt-password-label">Current Password</label>
            <div className="vt-password-input-wrapper">
              <div className="vt-password-input-icon">
                <KeyRound size={16} />
              </div>
              <input
                type={showPasswords.old ? 'text' : 'password'}
                name="oldPassword"
                value={formData.oldPassword}
                onChange={handleChange}
                placeholder="Enter your current password"
                className="vt-password-input"
              />
              <button
                type="button"
                className="vt-password-toggle"
                onClick={() => togglePasswordVisibility('old')}
                tabIndex="-1"
              >
                {showPasswords.old ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          
          <div className="vt-password-field">
            <label className="vt-password-label">New Password</label>
            <div className="vt-password-input-wrapper">
              <div className="vt-password-input-icon">
                <KeyRound size={16} />
              </div>
              <input
                type={showPasswords.new ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter your new password"
                className="vt-password-input"
              />
              <button
                type="button"
                className="vt-password-toggle"
                onClick={() => togglePasswordVisibility('new')}
                tabIndex="-1"
              >
                {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            
            {formData.newPassword && (
              <div className="vt-password-strength">
                <div className="vt-password-strength-bar">
                  <div
                    className="vt-password-strength-progress"
                    style={{ 
                      width: `${(passwordStrength / 5) * 100}%`,
                      backgroundColor: getStrengthColor()
                    }}
                  ></div>
                </div>
                <span 
                  className="vt-password-strength-text"
                  style={{ color: getStrengthColor() }}
                >
                  {getStrengthLabel()}
                </span>
              </div>
            )}
          </div>
          
          <div className="vt-password-field">
            <label className="vt-password-label">Confirm New Password</label>
            <div className="vt-password-input-wrapper">
              <div className="vt-password-input-icon">
                <Check size={16} />
              </div>
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your new password"
                className="vt-password-input"
              />
              <button
                type="button"
                className="vt-password-toggle"
                onClick={() => togglePasswordVisibility('confirm')}
                tabIndex="-1"
              >
                {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            
            {formData.newPassword && formData.confirmPassword && 
              formData.newPassword !== formData.confirmPassword && (
                <div className="vt-password-mismatch">
                  <AlertTriangle size={14} />
                  <span>Passwords don't match</span>
                </div>
              )
            }
          </div>
          
          <div className="vt-password-tips">
            <h3 className="vt-password-tips-title">Password Requirements</h3>
            <ul className="vt-password-tips-list">
              <li className={formData.newPassword && formData.newPassword.length >= 6 ? 'valid' : ''}>
                <div className="vt-password-tip-icon">
                  {formData.newPassword && formData.newPassword.length >= 6 ? <Check size={12} /> : ''}
                </div>
                At least 6 characters long
              </li>
              <li className={formData.newPassword && /[A-Z]/.test(formData.newPassword) ? 'valid' : ''}>
                <div className="vt-password-tip-icon">
                  {formData.newPassword && /[A-Z]/.test(formData.newPassword) ? <Check size={12} /> : ''}
                </div>
                At least one uppercase letter
              </li>
              <li className={formData.newPassword && /[0-9]/.test(formData.newPassword) ? 'valid' : ''}>
                <div className="vt-password-tip-icon">
                  {formData.newPassword && /[0-9]/.test(formData.newPassword) ? <Check size={12} /> : ''}
                </div>
                At least one number
              </li>
            </ul>
          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className={`vt-password-submit ${isLoading ? 'vt-password-submit-loading' : ''}`}
          >
            {isLoading ? (
              <>
                <span className="vt-password-spinner"></span>
                Updating Security...
              </>
            ) : (
              <>
                <Shield size={16} />
                Update Password
              </>
            )}
          </button>
        </form>
      </div>
      
      {toastNotification && (
        <HomeToastNotification
          message={toastNotification.message}
          type={toastNotification.type}
          onClose={() => setToastNotification(null)}
        />
      )}
      
      <style jsx>{`
        .vt-password-container {
          max-width: 500px;
          margin: 0 auto;
          padding: 0;
        }
        
        .vt-password-header {
          position: relative;
          text-align: center;
          padding: 30px 20px;
          margin-bottom: 20px;
          border-radius: 16px;
          background: rgba(79, 70, 229, 0.1);
          overflow: hidden;
          z-index: 1;
        }
        
        .vt-password-header-glow {
          position: absolute;
          top: -50px;
          left: 50%;
          transform: translateX(-50%);
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, rgba(79, 70, 229, 0.3) 0%, transparent 70%);
          z-index: -1;
        }
        
        .vt-password-header-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 60px;
          height: 60px;
          margin: 0 auto 16px;
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          border-radius: 50%;
          color: white;
          box-shadow: 0 4px 15px rgba(79, 70, 229, 0.4);
        }
        
        .vt-password-title {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 8px;
          color: #fff;
        }
        
        .vt-password-subtitle {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
        }
        
        .vt-password-card {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 24px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(10px);
        }
        
        .vt-password-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        .vt-password-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .vt-password-label {
          font-size: 14px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
        }
        
        .vt-password-input-wrapper {
          display: flex;
          align-items: center;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          transition: all 0.2s ease;
          position: relative;
        }
        
        .vt-password-input-wrapper:focus-within {
          border-color: rgba(79, 70, 229, 0.5);
          box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
        }
        
        .vt-password-input-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          color: rgba(255, 255, 255, 0.5);
        }
        
        .vt-password-input {
          flex: 1;
          background: transparent;
          border: none;
          color: white;
          padding: 12px 0;
          font-size: 14px;
          outline: none;
        }
        
        .vt-password-input::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }
        
        .vt-password-toggle {
          background: transparent;
          border: none;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          transition: color 0.2s;
          padding: 0;
        }
        
        .vt-password-toggle:hover {
          color: rgba(255, 255, 255, 0.8);
        }
        
        .vt-password-strength {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 8px;
        }
        
        .vt-password-strength-bar {
          flex: 1;
          height: 4px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 2px;
          overflow: hidden;
        }
        
        .vt-password-strength-progress {
          height: 100%;
          transition: width 0.3s ease, background-color 0.3s ease;
        }
        
        .vt-password-strength-text {
          font-size: 12px;
          font-weight: 500;
        }
        
        .vt-password-mismatch {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #f87171;
          margin-top: 6px;
        }
        
        .vt-password-tips {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 10px;
          padding: 16px;
          margin-top: 10px;
        }
        
        .vt-password-tips-title {
          font-size: 14px;
          font-weight: 600;
          margin: 0 0 12px;
          color: rgba(255, 255, 255, 0.8);
        }
        
        .vt-password-tips-list {
          list-style-type: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .vt-password-tips-list li {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.6);
          transition: color 0.3s ease;
        }
        
        .vt-password-tips-list li.valid {
          color: #34d399;
        }
        
        .vt-password-tip-icon {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #34d399;
          background: rgba(52, 211, 153, 0.1);
        }
        
        .vt-password-submit {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          color: white;
          border: none;
          border-radius: 10px;
          padding: 14px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 10px;
        }
        
        .vt-password-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
        }
        
        .vt-password-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .vt-password-submit-loading {
          cursor: not-allowed;
          opacity: 0.8;
        }
        
        .vt-password-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 8px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 480px) {
          .vt-password-header {
            padding: 20px 16px;
          }
          
          .vt-password-header-icon {
            width: 50px;
            height: 50px;
          }
          
          .vt-password-card {
            padding: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default ChangePassword;